import type { StepConfig, StepFormField, FieldValue, FieldValues } from '../../types/workflow';

const methodOptions = [
  { label: 'GET', value: 'get' },
  { label: 'POST', value: 'post' },
  { label: 'PUT', value: 'put' },
  { label: 'DELETE', value: 'delete' },
  { label: 'PATCH', value: 'patch' },
];

const contentTypeOptions = [
  { label: 'application/json', value: 'application/json' },
  { label: 'application/x-www-form-urlencoded', value: 'application/x-www-form-urlencoded' },
  { label: 'text/plain', value: 'text/plain' },
];

const shouldShowRequestBodySection = (value: FieldValue) => {
  return ['post', 'put', 'patch'].includes(value as string);
};

const httpRequest: StepConfig = {
  // Design
  icon: 'httpRequest',
  headerTextColor: '#49779D',
  headerColor: '#C3D4E3',
  title: 'HTTP Request',
  text: 'HTTP Request',
  category: 'Router',
  desc: 'Allows HTTP Requests to be sent & responses to be saved as variables.',
  primaryKey: 'url',
  // Variable fields configuration
  variableFields: [
    {
      fieldKey: 'responseMap',
      variableProperty: 'variable',
      isArray: true,
    },
  ],
  // Connector configuration
  connectors: {
    HttpSuccess: {
      headerTextColor: '#4CAF50',
      headerColor: '#E8F5E9',
      title: 'Success',
      category: 'Connector',
    },
    HttpFailure: {
      headerTextColor: '#F44336',
      headerColor: '#FFEBEE',
      title: 'Failure : Invalid',
      category: 'Connector',
    },
  },
  // Form
  formFields: [
    // HTTP Method and URL Section
    {
      key: 'method',
      type: 'select',
      label: 'HTTP Method',
      defaultValue: 'get',
      options: methodOptions,
      validation: {
        required: true,
      },
      onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
        // Show/hide request body accordion based on method
        const hasBody = shouldShowRequestBodySection(value);
        allFields['requestBodySection'].show = hasBody;
      },
    },
    {
      key: 'url',
      type: 'text',
      label: 'URL',
      placeholder: 'https://api.example.com/endpoint',
      defaultValue: '',
      validation: {
        required: true,
        pattern: /^https?:\/\/.+/,
        custom: (value: string) => {
          if (!value) {
            return 'URL is required';
          }
          if (!/^https?:\/\/.+/.test(value)) {
            return 'Please enter a valid URL starting with http:// or https://';
          }
          return null;
        },
      },
    },

    // Request Body and Content-Type Section (only shown for POST, PUT, PATCH)
    {
      key: 'requestBodySection',
      type: 'accordion',
      label: 'Request Body & Content-Type',
      defaultValue: true,
      show: (parentValues: Record<string, any>) => {
        return shouldShowRequestBodySection(parentValues.method);
      },
      fields: [
        {
          key: 'contentTypeHeader',
          type: 'select',
          label: 'Content-Type',
          defaultValue: 'application/json',
          options: contentTypeOptions,
        },
        {
          key: 'body',
          type: 'textarea',
          label: 'Request Body',
          placeholder: 'Enter request body (JSON, form data, etc.)',
          defaultValue: '',
          validation: {
            maxLength: 3000,
          },
        },
      ],
    },

    // Headers Section
    {
      key: 'headersSection',
      type: 'accordion',
      label: 'Headers',
      defaultValue: false,
      fields: [
        {
          key: 'headers',
          type: 'formRepeater',
          defaultValue: [],
          config: {
            maxItems: 10,
            minItems: 0,
            addButtonText: 'Add Header',
            showLogicalOperator: false,
            fields: [
              {
                key: 'key',
                type: 'text',
                placeholder: 'Key',
                defaultValue: '',
                validation: {
                  required: true,
                },
              },
              {
                key: 'value',
                type: 'text',
                placeholder: 'Value',
                defaultValue: '',
                validation: {
                  required: true,
                },
              },
            ],
          },
        },
      ],
    },
    // response mapping section
    {
      key: 'responseMappingSection',
      type: 'accordion',
      label: 'Response Mapping',
      defaultValue: false,
      fields: [
        {
          key: 'responseMap',
          type: 'formRepeater',
          defaultValue: [],
          config: {
            maxItems: 10,
            minItems: 0,
            addButtonText: 'Add Response Mapping',
            showLogicalOperator: false,
            fields: [
              {
                key: 'variable',
                type: 'text',
                placeholder: 'Variable',
                defaultValue: '',
                validation: {
                  required: true,
                },
              },
              {
                key: 'key',
                type: 'text',
                placeholder: 'Key',
                defaultValue: '',
                validation: {
                  required: true,
                },
              },
            ],
          },
        },
      ],
    },
  ],
};

export default httpRequest;
